import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../components/FormField.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import { providerTestErrorMessage } from '../../shared/provider-test-error.js';
import type {
  ProviderKeySummary,
  Tenant,
  TenantMediaRouteSummary,
  ToastMessage,
} from '../../types.js';

/** Image and video route settings share one tenant-wide data-policy draft.
 *  This prevents one route save from replaying stale policy values owned by
 *  the other route screen after PR-2 separates their destinations. */
export function MediaGenerationSettings({
  activeKind,
  http,
  tenantId,
  keys,
  mediaRoutes,
  tenant,
  busy,
  setBusy,
  onToast,
  onChanged,
}: {
  activeKind: 'image' | 'video';
  http: AdminHttpClient;
  tenantId: string;
  keys: ProviderKeySummary[];
  mediaRoutes: TenantMediaRouteSummary[];
  tenant: Tenant | undefined;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onToast: (toast: ToastMessage) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const translate = (key: string, options?: Record<string, unknown>): string =>
    String(options ? t(key, options) : t(key));
  const [testingProvider, setTestingProvider] = useState(false);
  const [imageRouteEnabled, setImageRouteEnabled] = useState(false);
  const [imageModel, setImageModel] = useState('openai/gpt-image-2');
  const [imageEndpointRegion, setImageEndpointRegion] = useState('');
  const [imageSupportsZdr, setImageSupportsZdr] = useState(false);
  const [imageSupportsMam, setImageSupportsMam] = useState(false);
  const [imageMaxUses, setImageMaxUses] = useState('5');
  const [videoRouteEnabled, setVideoRouteEnabled] = useState(false);
  const [videoModel, setVideoModel] = useState('sora-2');
  const [videoEndpointRegion, setVideoEndpointRegion] = useState('');
  const [videoMaxUses, setVideoMaxUses] = useState('5');
  const [requiredDataRegion, setRequiredDataRegion] = useState('');
  const [requireZdr, setRequireZdr] = useState(false);
  const [requireMam, setRequireMam] = useState(false);
  const [externalApisAllowed, setExternalApisAllowed] = useState(false);

  useEffect(() => {
    const imageRoute = mediaRoutes.find(
      (item) => item.kind === 'image' && item.provider === 'openai'
    );
    setImageRouteEnabled(imageRoute?.status === 'active');
    setImageModel(imageRoute?.model ?? 'openai/gpt-image-2');
    setImageEndpointRegion(imageRoute?.endpointRegion ?? tenant?.requiredDataRegion ?? '');
    setImageSupportsZdr(imageRoute?.supportsZdr ?? false);
    setImageSupportsMam(imageRoute?.supportsMam ?? false);
    setImageMaxUses(String(imageRoute?.maxUsesDefault ?? 5));

    const videoRoute = mediaRoutes.find(
      (item) => item.kind === 'video' && item.provider === 'openai'
    );
    setVideoRouteEnabled(videoRoute?.status === 'active');
    setVideoModel(videoRoute?.model ?? 'sora-2');
    setVideoEndpointRegion(videoRoute?.endpointRegion ?? tenant?.requiredDataRegion ?? '');
    setVideoMaxUses(String(videoRoute?.maxUsesDefault ?? 5));

    setRequiredDataRegion(tenant?.requiredDataRegion ?? '');
    setRequireZdr(tenant?.requireZdr ?? false);
    setRequireMam(tenant?.requireMam ?? false);
    setExternalApisAllowed(tenant?.externalApis === 'allow');
  }, [mediaRoutes, tenant]);

  async function testOpenAIConnection() {
    if (!tenantId.trim()) return;
    setTestingProvider(true);
    try {
      const result = await http.testProviderKey({
        tenantId: tenantId.trim(),
        provider: 'openai',
      });
      onToast(
        result.ok
          ? {
              tone: 'success',
              message: `openai: ${t('providerKeys.providerConnectionOk', {
                latency: result.latencyMs != null ? ` (${result.latencyMs}ms)` : '',
              })}`,
            }
          : {
              tone: 'error',
              message: `openai: ${providerTestErrorMessage(result, 'provider', translate)}`,
            }
      );
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setTestingProvider(false);
    }
  }

  async function saveImageRoute(event: FormEvent) {
    event.preventDefault();
    if (!tenantId.trim() || !imageModel.trim()) return;
    const parsedMaxUses = Number(imageMaxUses);
    setBusy(true);
    try {
      await http.setMediaRoute({
        tenantId: tenantId.trim(),
        kind: 'image',
        provider: 'openai',
        status: imageRouteEnabled ? 'active' : 'disabled',
        model: imageModel.trim(),
        ...(imageEndpointRegion.trim() ? { endpointRegion: imageEndpointRegion.trim() } : {}),
        supportsZdr: imageSupportsZdr,
        supportsMam: imageSupportsMam,
        ...(Number.isFinite(parsedMaxUses) ? { maxUsesDefault: parsedMaxUses } : {}),
        ...(requiredDataRegion.trim() ? { requiredDataRegion: requiredDataRegion.trim() } : {}),
        requireZdr,
        requireMam,
        externalApis: externalApisAllowed ? 'allow' : 'deny',
      });
      await onChanged();
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setBusy(false);
    }
  }

  async function saveVideoRoute(event: FormEvent) {
    event.preventDefault();
    if (!tenantId.trim() || !videoModel.trim()) return;
    const parsedMaxUses = Number(videoMaxUses);
    setBusy(true);
    try {
      await http.setMediaRoute({
        tenantId: tenantId.trim(),
        kind: 'video',
        provider: 'openai',
        status: videoRouteEnabled ? 'active' : 'disabled',
        model: videoModel.trim(),
        ...(videoEndpointRegion.trim() ? { endpointRegion: videoEndpointRegion.trim() } : {}),
        supportsZdr: false,
        supportsMam: false,
        ...(Number.isFinite(parsedMaxUses) ? { maxUsesDefault: parsedMaxUses } : {}),
        ...(requiredDataRegion.trim() ? { requiredDataRegion: requiredDataRegion.trim() } : {}),
        requireZdr,
        requireMam,
        externalApis: externalApisAllowed ? 'allow' : 'deny',
      });
      await onChanged();
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setBusy(false);
    }
  }

  const hasOpenAIKey = keys.some((item) => item.provider === 'openai' && item.hasCredential);
  const imageRouteBlockers = imageRouteEnabled
    ? [
        !hasOpenAIKey ? t('providerKeys.imageRouteBlock.providerKey') : undefined,
        !externalApisAllowed ? t('providerKeys.imageRouteBlock.externalApis') : undefined,
        !imageEndpointRegion.trim() ? t('providerKeys.imageRouteBlock.endpointRegion') : undefined,
        requiredDataRegion.trim() && imageEndpointRegion.trim() !== requiredDataRegion.trim()
          ? t('providerKeys.imageRouteBlock.regionMismatch')
          : undefined,
        requireZdr && !imageSupportsZdr ? t('providerKeys.imageRouteBlock.zdr') : undefined,
        requireMam && !imageSupportsMam ? t('providerKeys.imageRouteBlock.mam') : undefined,
      ].filter((message): message is string => Boolean(message))
    : [];
  const videoRouteBlockers = videoRouteEnabled
    ? [
        !hasOpenAIKey ? t('providerKeys.imageRouteBlock.providerKey') : undefined,
        !externalApisAllowed ? t('providerKeys.imageRouteBlock.externalApis') : undefined,
        !videoEndpointRegion.trim() ? t('providerKeys.imageRouteBlock.endpointRegion') : undefined,
        requiredDataRegion.trim() && videoEndpointRegion.trim() !== requiredDataRegion.trim()
          ? t('providerKeys.imageRouteBlock.regionMismatch')
          : undefined,
        requireZdr || requireMam ? t('providerKeys.videoDataControlsBlocked') : undefined,
      ].filter((message): message is string => Boolean(message))
    : [];

  return (
    <>
      <form
        className="panel form-grid"
        hidden={activeKind !== 'image'}
        style={activeKind !== 'image' ? { display: 'none' } : undefined}
        onSubmit={saveImageRoute}
      >
        <h2>{t('providerKeys.imageGeneration')}</h2>
        <p className="screen-help">{t('providerKeys.imageGenerationHelp')}</p>
        <span className={imageRouteEnabled ? 'badge active' : 'badge'}>
          {imageRouteEnabled ? t('providerKeys.active') : t('providerKeys.disabled')}
        </span>
        {imageRouteBlockers.length > 0 ? (
          <div className="warning">
            <strong>{t('providerKeys.imageRouteBlocked')}</strong>
            <ul>
              {imageRouteBlockers.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {!hasOpenAIKey ? <small>{t('providerKeys.providerKeyRequired')}</small> : null}
        <label className="check-row">
          <input
            type="checkbox"
            checked={imageRouteEnabled}
            onChange={(event) => setImageRouteEnabled(event.target.checked)}
          />
          <span>{t('providerKeys.enableImageGeneration')}</span>
        </label>
        <FormField
          label={t('providerKeys.imageModel')}
          value={imageModel}
          onChange={(event) => setImageModel(event.target.value)}
        />
        <FormField
          label={t('providerKeys.endpointRegion')}
          value={imageEndpointRegion}
          onChange={(event) => setImageEndpointRegion(event.target.value)}
        />
        <FormField
          label={t('providerKeys.requiredDataRegion')}
          value={requiredDataRegion}
          onChange={(event) => setRequiredDataRegion(event.target.value)}
        />
        <FormField
          label={t('providerKeys.maxUses')}
          type="number"
          min={1}
          max={20}
          value={imageMaxUses}
          onChange={(event) => setImageMaxUses(event.target.value)}
        />
        <label className="check-row">
          <input
            type="checkbox"
            checked={imageSupportsZdr}
            onChange={(event) => setImageSupportsZdr(event.target.checked)}
          />
          <span>{t('providerKeys.supportsZdr')}</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={imageSupportsMam}
            onChange={(event) => setImageSupportsMam(event.target.checked)}
          />
          <span>{t('providerKeys.supportsMam')}</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={requireZdr}
            onChange={(event) => setRequireZdr(event.target.checked)}
          />
          <span>{t('providerKeys.requireZdr')}</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={requireMam}
            onChange={(event) => setRequireMam(event.target.checked)}
          />
          <span>{t('providerKeys.requireMam')}</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={externalApisAllowed}
            onChange={(event) => setExternalApisAllowed(event.target.checked)}
          />
          <span>{t('providerKeys.allowExternalApis')}</span>
        </label>
        <div className="form-actions">
          <button
            type="button"
            className="secondary"
            disabled={!tenantId.trim() || !hasOpenAIKey || testingProvider}
            onClick={() => void testOpenAIConnection()}
          >
            {testingProvider ? t('providerKeys.testingProvider') : t('providerKeys.testProvider')}
          </button>
          <button
            disabled={
              busy ||
              !tenantId.trim() ||
              !imageModel.trim() ||
              (imageRouteEnabled && (!hasOpenAIKey || !imageEndpointRegion.trim()))
            }
          >
            {t('providerKeys.saveImageRoute')}
          </button>
        </div>
      </form>
      <form
        className="panel form-grid"
        hidden={activeKind !== 'video'}
        style={activeKind !== 'video' ? { display: 'none' } : undefined}
        onSubmit={saveVideoRoute}
      >
        <h2>{t('providerKeys.videoGeneration')}</h2>
        <p className="screen-help">{t('providerKeys.videoGenerationHelp')}</p>
        <span className={videoRouteEnabled ? 'badge active' : 'badge'}>
          {videoRouteEnabled ? t('providerKeys.active') : t('providerKeys.disabled')}
        </span>
        {videoRouteBlockers.length > 0 ? (
          <div className="warning">
            <strong>{t('providerKeys.videoRouteBlocked')}</strong>
            <ul>
              {videoRouteBlockers.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <label className="check-row">
          <input
            type="checkbox"
            checked={videoRouteEnabled}
            onChange={(event) => setVideoRouteEnabled(event.target.checked)}
          />
          <span>{t('providerKeys.enableVideoGeneration')}</span>
        </label>
        <label>
          {t('providerKeys.videoModel')}
          <select value={videoModel} onChange={(event) => setVideoModel(event.target.value)}>
            <option value="sora-2">sora-2</option>
          </select>
        </label>
        <FormField
          label={t('providerKeys.endpointRegion')}
          value={videoEndpointRegion}
          onChange={(event) => setVideoEndpointRegion(event.target.value)}
        />
        <FormField
          label={t('providerKeys.maxUses')}
          type="number"
          min={1}
          max={20}
          value={videoMaxUses}
          onChange={(event) => setVideoMaxUses(event.target.value)}
        />
        <button
          disabled={
            busy ||
            !tenantId.trim() ||
            !videoModel.trim() ||
            (videoRouteEnabled && videoRouteBlockers.length > 0)
          }
        >
          {t('providerKeys.saveVideoRoute')}
        </button>
      </form>
    </>
  );
}
